import { useEffect, useMemo, useState } from 'react';
import Reveal from '../components/Reveal';
import { visualHubPageAr, visualHubPageEn } from '../content/visualHubPageContent';
import { useLanguage } from '../context/LanguageContext';
import { getYouTubeEmbedUrl } from '../utils/getYouTubeEmbedUrl';

const heroBackgroundImage =
  'https://assets.weforum.org/project/image/mI0NhGRSfYpqygAwo9WKmNFpCtdVhgFGtpo3Y9NT-i0.jpeg';

const sectionImages = [
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=80',
  'https://cdn.discordapp.com/attachments/825507922590957588/1532783374278721689/ChatGPT_Image_Jul_31_2026_07_13_38_PM.png?ex=6a755b82&is=6a740a02&hm=16ce2fcafd17813c3fe389137453c5bc65c05562609a57e8b963abad5f7beb2a&',
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80',
  'https://tuckmagazine.com/wp-content/uploads/2016/12/dbdfndgmdgm.jpg',
];

const serviceThumbImages = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=900&q=80',
];

function YouTubeEmbed({ title, videoUrl, placeholder, hideCaption = false, thumbImage }) {
  const embedUrl = useMemo(() => getYouTubeEmbedUrl(videoUrl), [videoUrl]);
  const bg = thumbImage || heroBackgroundImage;

  return (
    <div className="visual-hub-video">
      <div className="visual-hub-video-frame">
        {embedUrl ? (
          <iframe
            title={title}
            src={`${embedUrl}?rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <div className="visual-hub-video-placeholder" style={{ backgroundImage: `url(${bg})` }}>
            <div className="visual-hub-video-placeholder-overlay" />
            <div className="visual-hub-video-placeholder-body">
              <span className="material-symbols-outlined visual-hub-video-placeholder-icon">
                play_circle
              </span>
              <span className="visual-hub-video-placeholder-label">{placeholder}</span>
            </div>
          </div>
        )}
      </div>
      {!hideCaption && title ? <h4 className="visual-hub-video-caption">{title}</h4> : null}
    </div>
  );
}

function VisualHubPage() {
  const { language, isArabic, t } = useLanguage();
  const page = language === 'ar' ? visualHubPageAr : visualHubPageEn;
  const [activeSectionId, setActiveSectionId] = useState(page.sections[0]?.id || '');

  useEffect(() => {
    setActiveSectionId(page.sections[0]?.id || '');
  }, [page]);

  const catalogServices = useMemo(() => {
    const groups = t.servicesCatalog?.groups || [];
    const sovereign = t.servicesCatalog?.sovereign;
    return [...groups.flatMap((group) => group.items), ...(sovereign ? [sovereign] : [])];
  }, [t.servicesCatalog]);

  const services = useMemo(() => {
    const byId = Object.fromEntries(page.services.map((item) => [item.id, item]));
    return catalogServices.map((service) => {
      const extra = byId[service.id] || {};
      return {
        id: service.id,
        title: service.name,
        text: service.marketing || '',
        icon: extra.icon || 'videocam',
        videoUrl: extra.videoUrl || '',
      };
    });
  }, [catalogServices, page.services]);

  const highlights = useMemo(
    () => [
      {
        value: String(page.sections.length),
        label: isArabic ? 'أقسام رئيسية' : 'Core sections',
      },
      {
        value: String(page.sections.reduce((sum, s) => sum + s.videos.length, 0)),
        label: isArabic ? 'فيديوهات أساسية' : 'Core videos',
      },
      {
        value: String(services.length),
        label: isArabic ? 'خدمة متكاملة' : 'Integrated services',
      },
      {
        value: '100',
        label: isArabic ? 'دولة' : 'Countries',
      },
    ],
    [page, isArabic, services.length]
  );

  const scrollToSection = (id) => {
    setActiveSectionId(id);
    const node = document.getElementById(`visual-hub-${id}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="inner-page visual-hub-page">
      <div className="visual-hub-atmosphere" aria-hidden="true" />

      <section className="visual-hub-hero-section">
        <div className="container">
          <Reveal>
            <div className="visual-hub-hero-card">
              <div
                className="visual-hub-hero-bg"
                style={{ backgroundImage: `url(${heroBackgroundImage})` }}
                aria-hidden="true"
              />
              <div className="visual-hub-hero-glow" aria-hidden="true" />
              <div className="visual-hub-hero-copy">
                <span className="eyebrow">{page.eyebrow}</span>
                <h1>{page.title}</h1>
                <div className="accent-line visual-hub-hero-line" />
                <p>{page.subtitle}</p>
              </div>
            </div>
          </Reveal>

          <div className="visual-hub-highlights">
            {highlights.map((item, i) => (
              <Reveal key={item.label} delay={100 + i * 80}>
                <article className="visual-hub-highlight-card">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section visual-hub-core">
        <div className="container">
          <Reveal>
            <div className="visual-hub-core-head">
              <span className="eyebrow center">{page.coreSectionsTitle}</span>
              <div className="visual-hub-section-tabs" role="tablist" aria-label={page.coreSectionsTitle}>
                {page.sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    role="tab"
                    aria-selected={activeSectionId === section.id}
                    className={`visual-hub-section-tab${
                      activeSectionId === section.id ? ' is-active' : ''
                    }`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {section.icon}
                    </span>
                    <span>{section.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="visual-hub-sections">
            {page.sections.map((section, index) => {
              const reverse = index % 2 === 1;
              return (
                <article
                  key={section.id}
                  id={`visual-hub-${section.id}`}
                  className={`visual-hub-section-block${reverse ? ' is-reverse' : ''}`}
                >
                  <div className="visual-hub-section-media" aria-hidden="true">
                    <div
                      className="visual-hub-section-image"
                      style={{ backgroundImage: `url(${sectionImages[index % sectionImages.length]})` }}
                    />
                    <div className="visual-hub-section-media-badge">
                      <span className="material-symbols-outlined">{section.icon}</span>
                      <strong>{String(index + 1).padStart(2, '0')}</strong>
                    </div>
                  </div>

                  <div className="visual-hub-section-main">
                    <Reveal direction={reverse ? 'left' : 'right'}>
                      <header className="visual-hub-section-head">
                        <span className="visual-hub-section-step">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div>
                          <h2>{section.title}</h2>
                          <p>{section.subtitle}</p>
                        </div>
                      </header>
                    </Reveal>

                    <div
                      className={`visual-hub-video-grid${
                        section.videos.length === 1 ? ' visual-hub-video-grid-single' : ''
                      }`}
                    >
                      {section.videos.map((video, videoIndex) => (
                        <Reveal
                          key={`${section.id}-${video.title}`}
                          delay={80 + videoIndex * 70}
                          direction="up"
                        >
                          <YouTubeEmbed
                            title={video.title}
                            videoUrl={video.videoUrl}
                            placeholder={page.videoPlaceholder}
                            thumbImage={sectionImages[(index + videoIndex) % sectionImages.length]}
                          />
                        </Reveal>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section visual-hub-services">
        <div className="container">
          <Reveal>
            <div className="visual-hub-services-banner">
              <div
                className="visual-hub-services-banner-bg"
                style={{
                  backgroundImage:
                    "url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1800&q=80')",
                }}
                aria-hidden="true"
              />
              <div className="visual-hub-services-banner-overlay" aria-hidden="true" />
              <div className="visual-hub-services-banner-copy">
                <span className="eyebrow">{page.servicesTitle}</span>
                <h2>{page.servicesTitle}</h2>
                <p>{page.servicesSubtitle}</p>
              </div>
            </div>
          </Reveal>

          <div className="visual-hub-services-grid">
            {services.map((service, index) => (
              <Reveal key={service.id} delay={(index % 4) * 60}>
                <article className="visual-hub-service-card">
                  <div className="visual-hub-service-meta">
                    <span className="visual-hub-service-number">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {service.icon}
                    </span>
                  </div>
                  <YouTubeEmbed
                    title={service.title}
                    videoUrl={service.videoUrl}
                    placeholder={page.videoPlaceholder}
                    hideCaption
                    thumbImage={serviceThumbImages[index % serviceThumbImages.length]}
                  />
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default VisualHubPage;
